import { ActionIcon, Alert, Box, Center, Loader, ScrollArea, Stack, Text } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { closeModal, ContextModalProps } from "@mantine/modals";
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from "@stripe/react-stripe-js";
// ВНИМАНИЕ: мы не хотим грузить страйп пока не надо.
// https://github.com/stripe/stripe-js/issues/43
// Тип Stripe импортируем из основного модуля (это только типы, не вызывает загрузку)
import type { Stripe } from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js/pure";
import { IconAlertCircle, IconCreditCard, IconX } from "@tabler/icons-react";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";


export type ModalInnerProps = {
	paymentSecret: string;
	publishableKey: string;
	returnUrl: string;
	email?: string | null | undefined;
	onCompleted: () => void;
	onError: (message: string) => void;
};

type LoadingState = "loading" | "ready" | "error" | "completed";

/**
 * Компонент формы оплаты через Stripe Embedded Checkout.
 * Включает состояния загрузки, ошибки и успешного завершения.
 */
export const StripeCheckoutForm = (props: ContextModalProps<ModalInnerProps>) => {
	const { t } = useTranslation();
	const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
	const [loadingState, setLoadingState] = useState<LoadingState>("loading");
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	// Определяем мобильный режим для fullscreen стилей
	const isMobile = useMediaQuery("(max-width: 50em)");

	// Загрузка Stripe SDK
	useEffect(() => {
		let isMounted = true;

		const initStripe = async () => {
			try {
				log.info("Loading Stripe SDK");
				const stripeInstance = loadStripe(props.innerProps.publishableKey);

				if (isMounted) {
					setStripePromise(stripeInstance);

					// Проверяем что Stripe загрузился успешно
					const stripe = await stripeInstance;
					if (!stripe && isMounted) {
						throw new Error("Failed to load Stripe");
					}

					if (isMounted) {
						setLoadingState("ready");
						log.info("Stripe SDK loaded successfully");
					}
				}
			} catch (error) {
				log.error("Failed to load Stripe SDK", error);
				if (isMounted) {
					setLoadingState("error");
					setErrorMessage(t("stripe_checkout.load_error", "Failed to load payment system"));
					props.innerProps.onError("Failed to load Stripe SDK");
				}
			}
		};

		void initStripe();

		return () => {
			isMounted = false;
		};
	}, [props.innerProps.publishableKey]);

	// Обработчик завершения платежа
	const handleComplete = useCallback(() => {
		log.info("Payment completed successfully");
		setLoadingState("completed");
		props.innerProps.onCompleted();

		// Закрываем модалку через секунду для отображения сообщения об успехе
		setTimeout(() => {
			log.info("Closing modal %s after completion", props.id);
			closeModal(props.id);
		}, 1500);
	}, [props.id, props.innerProps]);

	// Опции для EmbeddedCheckoutProvider
	const options = {
		clientSecret: props.innerProps.paymentSecret,
		onComplete: handleComplete
	};

	// Обработчик закрытия модалки
	const handleClose = useCallback(() => {
		closeModal(props.id);
	}, [props.id]);

	// Кнопка закрытия
	const CloseButton = () => (
		<ActionIcon
			onClick={handleClose}
			variant="subtle"
			color="dark"
			size="md"
			radius="xl"
			m={"sm"}
			style={{
				boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)"
			}}
			aria-label={t("stripe_checkout.close", "Close")}
		>
			<IconX color={"gray"} size={16} />
		</ActionIcon>
	);

	// Состояние загрузки
	if (loadingState === "loading" || !stripePromise) {
		return (
			<Box p="md">
				<Box style={{ display: "flex", justifyContent: "flex-end" }}>
					<CloseButton />
				</Box>
				<Center py="xl">
					<Stack align="center" gap="md">
						<Loader size="lg" />
						<Text size="sm" c="dimmed">
							{t("stripe_checkout.loading", "Loading payment form...")}
						</Text>
					</Stack>
				</Center>
			</Box>
		);
	}

	// Состояние ошибки
	if (loadingState === "error") {
		return (
			<Box p="md">
				<Box style={{ display: "flex", justifyContent: "flex-end" }}>
					<CloseButton />
				</Box>
				<Alert
					icon={<IconAlertCircle size={16} />}
					title={t("stripe_checkout.error_title", "Payment Error")}
					color="red"
					variant="filled"
				>
					{errorMessage || t("stripe_checkout.generic_error", "An error occurred while loading the payment form")}
				</Alert>
			</Box>
		);
	}

	// Состояние успешного завершения
	if (loadingState === "completed") {
		return (
			<Box p="md">
				<Alert
					icon={<IconCreditCard size={16} />}
					title={t("stripe_checkout.success_title", "Payment Successful")}
					color="green"
					variant="filled"
				>
					{t("stripe_checkout.success_message", "Your payment has been processed successfully")}
				</Alert>
			</Box>
		);
	}

	// Форма оплаты - кнопка закрытия sticky при скролле
	return (
		<Box pos="relative" style={{ height: isMobile ? "100vh" : "auto" }}>
			{/* Кнопка закрытия поверх контента */}
			<Box
				style={{
					position: "absolute",
					top: 8,
					right: 8,
					zIndex: 10
				}}
			>
				<CloseButton />
			</Box>
			<ScrollArea.Autosize
				mah={isMobile ? "100vh" : "80vh"}
				type="always"
				scrollbars="y"
				style={{
					backgroundColor: "white",
					borderRadius: isMobile ? 0 : "8px",
					height: isMobile ? "100vh" : "auto"
				}}
				styles={{
					scrollbar: { backgroundColor: "white" },
					thumb: { backgroundColor: "#dee2e6" }
				}}
			>
				<Box
					id="checkout"
					style={{
						overflow: "hidden",
						minHeight: isMobile ? "100vh" : "400px"
					}}
				>
					<EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
						<EmbeddedCheckout />
					</EmbeddedCheckoutProvider>
				</Box>
			</ScrollArea.Autosize>
		</Box>
	);
};

/**
 * Тестовый компонент для отладки Stripe Checkout.
 * Получает параметры из URL query string.
 */
export const StripeCheckoutFormTest = (props: ContextModalProps<ModalInnerProps>) => {
	const { t } = useTranslation();
	const location = useLocation();
	const searchParams = new URLSearchParams(location.search);

	const paymentSecret = searchParams.get("paymentSecret");
	const publishableKey = searchParams.get("publishableKey");

	if (!paymentSecret || !publishableKey) {
		return (
			<Alert
				icon={<IconAlertCircle size={16} />}
				title={t("stripe_checkout.missing_params_title", "Missing Parameters")}
				color="red"
				variant="light"
			>
				{t("stripe_checkout.missing_params", "Missing payment secret or publishable key in URL")}
			</Alert>
		);
	}

	return (
		<StripeCheckoutForm
			{...props}
			innerProps={{
				...props.innerProps,
				paymentSecret,
				publishableKey,
				returnUrl: window.location.origin + "/payment-result",
				onCompleted: () => {
					log.info("Test payment completed");
				},
				onError: (message: string) => {
					log.error("Test payment error:", message);
				}
			}}
		/>
	);
};
